import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl, CATEGORIES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSharedLocation } from "@/context/LocationContext";
import LocationFilterBar, { UnknownLocationHeading, DistanceBadge } from "@/components/LocationFilterBar";
import { SaveToListButton, useSavedItems } from "@/components/SaveToList";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, MapPin, Store, Pencil } from "lucide-react";

export default function VendorDirectory() {
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(true);
  const { loc, radius } = useSharedLocation();
  const { isParent, saved, add } = useSavedItems();
  const unknownStart = vendors.findIndex((v) => v.distance_km == null);

  useEffect(() => {
    let stale = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (category) params.set("category", category);
    if (loc) {
      params.set("lat", loc.lat);
      params.set("lng", loc.lng);
      params.set("radius_km", radius);
    }
    api.get(`/vendors?${params.toString()}`)
      .then((r) => { if (!stale) setVendors(r.data); })
      .catch(() => {})
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [search, category, loc, radius]);

  return (
    <div data-testid="vendor-directory">
      {user?.role === "vendor" && (
        <div className="flex justify-end mb-4">
          <Link to="/vendor-portal">
            <Button variant="outline" className="rounded-full border-sage/40 text-sage-dark hover:bg-sage-light" data-testid="edit-my-listing-btn">
              <Pencil className="w-4 h-4 mr-2" /> Edit my listing
            </Button>
          </Link>
        </div>
      )}

      <div className="relative mb-5 max-w-xl">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-forest/40" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, keyword or location..."
          className="pl-11 rounded-full bg-white/80 border-sage/30 h-12"
          data-testid="vendor-search-input"
        />
      </div>

      <div className="flex gap-2 overflow-x-auto pb-3 mb-8 -mx-1 px-1" data-testid="category-filters">
        <Button
          size="sm"
          onClick={() => setCategory("")}
          className={`rounded-full shrink-0 ${!category ? "bg-sage text-white hover:bg-sage-dark" : "bg-white/70 text-forest hover:bg-sage-light border border-sage/30"}`}
          data-testid="filter-all"
        >
          All
        </Button>
        {CATEGORIES.map((c) => (
          <Button
            key={c}
            size="sm"
            onClick={() => setCategory(category === c ? "" : c)}
            className={`rounded-full shrink-0 ${category === c ? "bg-sage text-white hover:bg-sage-dark" : "bg-white/70 text-forest hover:bg-sage-light border border-sage/30"}`}
            data-testid={`filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          >
            {c}
          </Button>
        ))}
      </div>

      <LocationFilterBar testPrefix="vendors" noun="vendors" />

      {loading ? (
        <div className="text-center py-20 text-forest/50" data-testid="vendors-loading">Loading vendors...</div>
      ) : vendors.length === 0 ? (
        <div className="text-center py-20 bg-white/60 border border-sage/20 rounded-3xl" data-testid="vendors-empty">
          <Store className="w-10 h-10 text-sage mx-auto mb-3" />
          <p className="text-forest/60">No vendors match your search yet. Try a different keyword or category.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {vendors.map((v, i) => {
            const cardId = v.card_id || v.vendor_id;
            return (
            <Fragment key={cardId}>
            {loc && i === unknownStart && <UnknownLocationHeading testid="vendors-unknown-location-heading" />}
            <Link
              to={`/vendors/${v.vendor_id}`}
              className={`fade-in-${Math.min(i % 4, 3)} group bg-white/80 border border-sage/20 rounded-3xl overflow-hidden shadow-sm hover:shadow-md hover:scale-[1.02] transition-[transform,box-shadow]`}
              data-testid={`vendor-card-${cardId}`}
            >
              <div className="h-36 bg-sage-light flex items-center justify-center overflow-hidden">
                {v.logo_path ? (
                  <img src={fileUrl(v.logo_path)} alt={v.business_name} className="w-full h-full object-contain" />
                ) : (
                  <img src="/logo.png" alt="Compass Kids logo" className="max-h-20 max-w-[70%] object-contain opacity-80" data-testid={`vendor-logo-fallback-${cardId}`} />
                )}
              </div>
              <div className="p-5">
                <h3 className="font-heading font-bold text-lg text-forest leading-snug">{v.business_name}</h3>
                {v.branch_name && (
                  <p className="text-xs font-bold text-sage-dark uppercase tracking-wide mt-1" data-testid={`vendor-branch-name-${cardId}`}>
                    {v.branch_name}
                  </p>
                )}
                {v.tagline && <p className="text-sm text-forest/60 mt-1 line-clamp-2">{v.tagline}</p>}
                {v.location && (
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(v.location)}`, "_blank", "noopener,noreferrer"); }}
                    className="flex items-center gap-1 text-xs text-sage-dark hover:text-terracotta-dark hover:underline mt-2 transition-colors"
                    data-testid={`vendor-location-link-${cardId}`}
                    aria-label={`Open ${v.location} in Google Maps`}
                  >
                    <MapPin className="w-3.5 h-3.5" /> {v.location}
                  </button>
                )}
                <div className="flex flex-wrap gap-1.5 mt-3">
                  <DistanceBadge km={v.distance_km} testid={`vendor-distance-${cardId}`} />
                  {(v.categories || []).slice(0, 3).map((c) => (
                    <span key={c} className="text-[10px] font-bold uppercase tracking-wide bg-terracotta-light text-terracotta-dark px-2 py-0.5 rounded-full">{c}</span>
                  ))}
                </div>
                {isParent && <SaveToListButton itemType="vendor" itemId={v.vendor_id} saved={saved} add={add} />}
              </div>
            </Link>
            </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
